/**
 * @file index.tsx
 *
 * @fileoverview Renders a contact request button for a resume that encapsulates all
 * the necessary behavior of a contact request feature.
 */
import React, { useContext, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import ResumeContactBadge from '~/jobiqo/components/ResumeContact/ResumeContactBadge';
import { UserContext, UserContextInterface } from '~/jobiqo/context/user';
import { analyticsDispatch, EVENTS } from '~/jobiqo/lib/analytics';
import { isRecruiter } from '~/jobiqo/lib/auth';
import { Resume } from '~/jobiqo/types/types';
import { Button } from '~/vcl/atoms/01-forms/01-button';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { Modal } from '~/vcl/atoms/15-modal';
import { useMutation } from '@apollo/client';
import CREATE_CONTACT_REQUEST from '~/jobiqo/components/Resume/ContactRequest/graphql/mutations/createResumeContactRequest.gql';
import { useFlags } from '~/jobiqo/hooks/flags';
import { MessageAlert } from '~/vcl/atoms/02-messages/01-alert';
import { useRefetchResumeSearchQuery } from '../../ResumeSearch/graphql/getResumeSearchQuery';
import { LoadingDotsIcon } from '~/vcl/icons/LoadingDots';
import { useConfig } from '~/jobiqo/context/config';
import { calculateUserSubscriptionValues, showModalCalculation } from './utils';
import ContactRequestConfirmation from '~/jobiqo/components/Resume/ContactRequest/ConfirmationAlert';
import NoSlotsModal from './modals/NoSlotsModal';
import NoCreditsModal from './modals/NoCreditsModal';
import ContactRequestFormModal from './modals/ContactRequestFormModal';
import { getResumeAnalyticsDimensions } from '~/jobiqo/components/Resume/resume_analytics';

export interface ContactRequestProps {
  /**
   * The resume.
   */
  resume: Resume;
  /**
   * If contact requests need approval from applicants.
   *
   * @default true
   */
  needsApproval?: boolean;
}

export function ContactRequest({
  resume,
  needsApproval = true,
}: ContactRequestProps) {
  let ErrorList: React.ReactNode = null;
  const {
    user,
    credits,
    userSubscriptions,
    resetUserContext,
  }: UserContextInterface = useContext(UserContext);
  const flagService = useFlags();
  const intl = useIntl();
  const [openModal, setOpenModal] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const { getConfigByName } = useConfig();
  const RESUME_SEARCHES_FEATURE_ACTIVE: boolean = !!getConfigByName(
    'resume_search_enabled',
  )?.value[0];
  const RESUME_CONTACT_FEATURE_ACTIVE: boolean = !!getConfigByName(
    'resume_contact_enabled',
  )?.value[0];

  const { refetchResumeSearchQuery } = useRefetchResumeSearchQuery();
  const [createResumeContact, { data, loading, error }] = useMutation(
    CREATE_CONTACT_REQUEST,
    {
      onCompleted: (completeData) => {
        if (completeData.createResumeContact.errors?.length === 0) {
          if (user && isRecruiter(user)) {
            analyticsDispatch(
              EVENTS.RESUME_REQUEST,
              getResumeAnalyticsDimensions(resume, user),
            );
          }
          flagService.addFlag({
            type: 'success',
            text: needsApproval
              ? intl.formatMessage({
                  id: 'contact_request_create_success',
                  defaultMessage: 'Contact request sent',
                })
              : intl.formatMessage({
                  id: 'contact_request_no_approval_create_success',
                  defaultMessage: 'Access granted',
                }),
            test_id: 'contact_request_create_success',
          });
        }
      },
    },
  );

  if (error) {
    ErrorList = (
      <MessageAlert alertStyle="danger" type="assertive">
        <FormattedMessage
          id="contact_request_create_contact_error"
          defaultMessage="Error creating your contact request."
        />
      </MessageAlert>
    );
  }

  if (data?.createResumeContact?.errors?.length > 0) {
    ErrorList = data.createResumeContact.errors[0].message;
  }

  const {
    hasFlatrateSubscription,
    userResumeContactSlotsAvailable,
    userResumeContactSlotsActive,
    userResumeCredits,
  } = calculateUserSubscriptionValues(userSubscriptions, credits);

  const hasFlatRateAndNoCredits =
    hasFlatrateSubscription &&
    userResumeContactSlotsActive &&
    userResumeContactSlotsAvailable <= 0;

  const hasNoFlatRateAndNoCredits =
    userResumeCredits <= 0 && !hasFlatrateSubscription;

  const hasFlatRateAndCreditsAvailable =
    hasFlatrateSubscription &&
    userResumeContactSlotsActive &&
    userResumeContactSlotsAvailable > 0;

  const shouldShowModal = showModalCalculation(
    needsApproval,
    hasFlatrateSubscription,
    userResumeContactSlotsActive,
    userResumeContactSlotsAvailable,
    userResumeCredits,
  );

  // Handles submitting a contact request. If needs approval is set it comes from the
  // contact request form, otherwise creates it automatically (no message).
  function handleCreateContact(event?: React.FormEvent<HTMLFormElement>) {
    createResumeContact({
      variables: {
        resumeId: resume.id,
        organizationId: user?.activeOrganization?.id,
        message: event ? event.target[0].value : '',
      },
      update(cache) {
        cache.evict({ fieldName: 'resumeByUserId' });
        cache.gc();
        resetUserContext();
      },
      refetchQueries: RESUME_SEARCHES_FEATURE_ACTIVE
        ? [refetchResumeSearchQuery]
        : [],
    });
  }

  return user && isRecruiter(user) && RESUME_CONTACT_FEATURE_ACTIVE ? (
    <>
      {ErrorList && (
        <>
          {ErrorList}
          <Spacing />
        </>
      )}
      {!resume.resumeContact && !resume.privateFieldsAccess ? (
        <Button
          bordered={true}
          buttonStyle="secondary"
          data-testid={`jbq-contact-request-${resume.id}`}
          onClick={(e) => {
            if (shouldShowModal) {
              // If needs approval is on we show the Contact request form.
              setOpenModal(true);
            } else {
              if (hasFlatrateSubscription) {
                handleCreateContact();
              } else {
                // If needs approval is off we directly create the resume contact.
                setOpenConfirmDialog(true);
              }
            }
          }}
        >
          {loading ? (
            <LoadingDotsIcon />
          ) : needsApproval ? (
            <FormattedMessage
              id="contact_request_button_text"
              defaultMessage="Contact"
            />
          ) : (
            <FormattedMessage
              id="contact_request_no_need_approval"
              defaultMessage="Request access"
            />
          )}
        </Button>
      ) : (
        resume.resumeContact && (
          <ResumeContactBadge
            resumeContact={resume.resumeContact}
            needsApproval={needsApproval}
          />
        )
      )}
      <Modal
        isShown={!!openModal}
        onHide={() => {
          setOpenModal(false);
        }}
      >
        {credits.Error && (
          <>
            {credits.Error}
            <Spacing />
          </>
        )}
        {userSubscriptions.Error && (
          <>
            {userSubscriptions.Error}
            <Spacing />
          </>
        )}
        {hasFlatRateAndNoCredits && <NoSlotsModal />}
        {hasNoFlatRateAndNoCredits && <NoCreditsModal />}
        {(hasFlatRateAndCreditsAvailable ||
          (hasFlatrateSubscription && !userResumeContactSlotsActive) ||
          (userResumeCredits > 0 && !hasFlatrateSubscription)) && (
          <ContactRequestFormModal
            resume={resume}
            isSubmitting={loading}
            onComplete={(event) => {
              setOpenModal(false);
              handleCreateContact(event);
            }}
            ErrorList={ErrorList}
          />
        )}
      </Modal>
      <ContactRequestConfirmation
        openConfirmDialog={openConfirmDialog}
        userResumeCredits={userResumeCredits}
        onConfirm={() => {
          handleCreateContact();
          // Refetch user credits.
          resetUserContext();
          setOpenConfirmDialog(false);
        }}
        onClose={() => setOpenConfirmDialog(false)}
      />
    </>
  ) : null;
}
export default ContactRequest;
